import React from 'react';
import { useNavigate } from 'react-router-dom';

import { AttentionMessage } from '@eika/attention-message';
import { ButtonTypes, IconButton } from '@eika/button';
import { SummaryCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { RichIconTypes } from '@eika/rich-icon';

import { NavigationButtons } from '../../../../components/shared/NavigationButtons';
import { PaymentRetryContext } from '../../../../components/transaction/PaymentRetry';
import TransactionResultInfo from '../../../../components/transaction/TransactionResultInfo';
import useShowPaymentResult from '../../../../hooks/useShowPaymentResult';
import useTranslation, { format, formatJsx } from '../../../../hooks/useTranslation';
import SigningContextFlow from '../../../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../../../signingContext/signingContext';
import { Actions } from '../../../../signingContext/signingTypes';
import { eFakturaTrackingIDs } from '../../../../utils/gtm/trackIDs/eFaktura';
import createAddPermissionRequest from '../../form/createAddPermissionRequest';
import useFormContext from '../../form/useFormContext';
import formatOrgNumber from '../../format/formatOrgNumber';
import useCanIBeHere from '../../hooks/useCanIBeHere';
import routes from '../../routes';

import './Summary.scss';

const Summary: React.FC = () => {
    const translated = useTranslation();
    const navigate = useNavigate();
    const {
        form: { state, handleChange, checkFormValidity, errors },
    } = useFormContext();

    const {
        signingOperationPending,
        showPaymentRetry,
        startSigning,
        signingStatus,
        signingError,
        handleCancelSigningFlow,
        dispatch,
    } = useSigningContext(createAddPermissionRequest);

    useCanIBeHere(['name', 'ssn', 'amountSelected', 'selected']);

    const showPaymentResult = useShowPaymentResult();

    const onClose = () => navigate(routes.home, { replace: true });

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (await checkFormValidity()) {
            startSigning(state);
        }
    };

    if (showPaymentRetry) {
        return <PaymentRetryContext />;
    }

    if (showPaymentResult) {
        return (
            <TransactionResultInfo
                type="efaktura-sharing"
                status={signingStatus}
                error={signingError}
                retry={signingError ? handleCancelSigningFlow : onClose}
                onClose={onClose}
                onRetryManually={() => dispatch({ type: Actions.CLEAR_ERROR })}
                successButtons
            />
        );
    }

    return (
        <SigningContextFlow>
            <form onSubmit={onSubmit} className="efaktura-sharing__summary-wrapper">
                <SummaryCard icon={RichIconTypes.HJERTE_M_HENDER} className="efaktura-sharing__summary-card">
                    <p>
                        {formatJsx(
                            translated.eFakturaSharing.confirmationText[state.amountSelected ?? 'all'],
                            <span className="bold">
                                {state.name}, {state.ssn}
                            </span>,
                        )}
                    </p>
                    {state.amountSelected === 'some' && (state.selected ?? []).length > 0 && (
                        <ol className="efaktura-sharing__creditor-list">
                            {state.selected?.map((creditor) => (
                                <li className="efaktura-sharing__creditor-list-item" key={creditor.creditorId.value}>
                                    <p className="efaktura-sharing__creditor-list-item__name">{creditor.name}</p>
                                    <span className="efaktura-sharing__creditor-list-item__org-number-label">
                                        {translated.eFakturaSharing.organisationNumber}
                                    </span>
                                    <span className="efaktura-sharing__creditor-list-item__org-number">
                                        {formatOrgNumber(creditor.creditorId)}
                                    </span>
                                    <IconButton
                                        buttonType={ButtonTypes.ICON_NEGATIVE}
                                        className="efaktura-sharing__creditor-list-item__garbage"
                                        screenReaderText={format(
                                            translated.eFakturaSharing.deleteCompanyFromShareList,
                                            creditor.name,
                                        )}
                                        icon={IconTypes.SLETT_24}
                                        onClick={() =>
                                            handleChange({
                                                type: 'selected',
                                                payload: state.selected?.filter(
                                                    (x) => x.creditorId.value !== creditor.creditorId.value,
                                                ),
                                            })
                                        }
                                        disabled={signingOperationPending}
                                    />
                                </li>
                            ))}
                        </ol>
                    )}
                    {state.amountSelected === 'some' && (errors.selected || (state.selected ?? []).length === 0) && (
                        <div className="efaktura-sharing__creditor-list">
                            <AttentionMessage
                                type="error"
                                text={
                                    errors.selected ??
                                    translated.eFakturaSharing.validationErrors.selected.tooFewSelected
                                }
                            />
                        </div>
                    )}
                    <p className="efaktura-sharing__summary-notice">{translated.eFakturaSharing.summaryNotice}</p>
                </SummaryCard>
                <NavigationButtons
                    className="efaktura-sharing__navigation-buttons"
                    centerOnSmallerWidth
                    paymentButton
                    paymentButtonText={translated.eFakturaSharing.giveAccess}
                    cancelUrl={routes.cancel}
                    paymentOperationPending={signingOperationPending}
                    dataTrackId={eFakturaTrackingIDs.shareEFaktura.id}
                />
            </form>
        </SigningContextFlow>
    );
};

export default Summary;
